import { zodResolver } from "@hookform/resolvers/zod";
import ConfirmProductionDeviationModal from "features/CenterContent/RoleContent/ProductionDeviation/steps/ProductionDeviationConfirm";
import { ProductionDeviationFormInputs } from "features/CenterContent/RoleContent/ProductionDeviation/steps/ProductionDeviationFormInputs";
import { ProductionDeviationOverview } from "features/CenterContent/RoleContent/ProductionDeviation/steps/ProductionDeviationOverview";
import { SelectTrainIdsModal } from "features/CenterContent/RoleContent/ProductionDeviation/steps/SelectTrainIdsModal";
import { SelectTrainSeries } from "features/CenterContent/RoleContent/ProductionDeviation/steps/SelectTrainSeries";
import {
  getFormSchema,
  ProductionDeviationForm,
} from "features/CenterContent/RoleContent/ProductionDeviation/types/formSchema";
import { ProductionDeviationRequest } from "features/CenterContent/RoleContent/ProductionDeviation/types/requestSchema";
import { formToRequest } from "features/CenterContent/RoleContent/ProductionDeviation/types/transformers";
import {
  submitSkipEmptyDirections,
  triggerSkipEmptyDirections,
  triggerDurationsTypeAndReason,
  useProductionDeviationPath,
  useTrainSeries,
} from "features/CenterContent/RoleContent/ProductionDeviation/utils";
import { TrainInfoProvider } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/useTrainInfoContext";
import { useOperationalInformationTypes } from "features/CenterContent/shared/operationalInformation/hooks/useOperationalInformationTypes";
import { FC, useState } from "react";
import { DefaultValues, FormProvider, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useFormVerbose } from "shared/form/useFormVerbose";

type ProductionDeviationModalProps = {
  post: (request: ProductionDeviationRequest) => void;
  status: "error" | "idle" | "pending" | "success";
  onClose: () => void;
  defaultValues?: DefaultValues<ProductionDeviationForm>;
};

export enum ProductionDeviationSteps {
  TRAINSERIES,
  FORM,
  TRAINIDS,
  OVERVIEW,
  CONFIRMED,
}

const ProductionDeviationModalContent: FC<ProductionDeviationModalProps> = ({
  post,
  status,
  onClose,
  defaultValues,
}) => {
  const { t } = useTranslation();
  const opInfoTypes = useOperationalInformationTypes(t);

  const isEditMode = !!defaultValues;

  const [activeStep, setActiveStep] = useState(
    isEditMode
      ? ProductionDeviationSteps.FORM
      : ProductionDeviationSteps.TRAINSERIES,
  );
  const [trainSeriesIndex, setTrainSeriesIndex] = useState(0);

  const [trainSeries, setTrainSeries] = useTrainSeries();

  const trainSerie = trainSeries[trainSeriesIndex];
  const { path } = useProductionDeviationPath(trainSerie);

  const formContext = useFormContext<ProductionDeviationForm>();

  const { watch, handleSubmit } = formContext;

  const onSubmit = handleSubmit((data) => post(formToRequest(data)));

  const selectedTrains = watch(path("selectedTrains"));

  if (activeStep === ProductionDeviationSteps.TRAINSERIES) {
    return (
      <SelectTrainSeries
        isEditMode={isEditMode}
        onClose={onClose}
        onSubmit={() => setActiveStep(ProductionDeviationSteps.FORM)}
        trainSeries={trainSeries}
        setTrainSeries={setTrainSeries}
      />
    );
  }

  if (activeStep === ProductionDeviationSteps.FORM) {
    return (
      <ProductionDeviationFormInputs
        isEditMode={isEditMode}
        path={path}
        onClose={onClose}
        onBack={() => setActiveStep(ProductionDeviationSteps.TRAINSERIES)}
        onSubmit={() => {
          setTrainSeriesIndex(0);
          setActiveStep(ProductionDeviationSteps.TRAINIDS);
        }}
        opInfoTypes={opInfoTypes}
        trainSeries={trainSeries}
        trigger={() =>
          triggerDurationsTypeAndReason(formContext, path, isEditMode)
        }
      />
    );
  }

  if (activeStep === ProductionDeviationSteps.TRAINIDS) {
    return (
      <TrainInfoProvider>
        <SelectTrainIdsModal
          isEditMode={isEditMode}
          trainSeries={trainSeries}
          opInfoData={opInfoTypes.data!} // Must be defined since it was used in the previous step
          setActiveStep={setActiveStep}
          selectedTrains={selectedTrains}
          onClose={onClose}
          trainSeriesIndex={trainSeriesIndex}
          setTrainSeriesIndex={setTrainSeriesIndex}
          trigger={() => triggerSkipEmptyDirections(formContext, path)}
        />
      </TrainInfoProvider>
    );
  }
  if (activeStep === ProductionDeviationSteps.OVERVIEW) {
    return (
      <TrainInfoProvider>
        <ProductionDeviationOverview
          isEditMode={isEditMode}
          onClose={() => {
            if (status === "success") {
              setActiveStep(ProductionDeviationSteps.CONFIRMED);
            } else {
              onClose();
            }
          }}
          onSubmit={() => submitSkipEmptyDirections(formContext, onSubmit)}
          setActiveStep={setActiveStep}
          trainSeriesIndex={trainSeriesIndex}
          setTrainSeriesIndex={setTrainSeriesIndex}
          trainSeries={trainSeries}
          opInfoData={opInfoTypes.data!} // Must be defined since it was used in the previous step
          status={status}
        />
      </TrainInfoProvider>
    );
  }
  return (
    <ConfirmProductionDeviationModal
      isEditMode={isEditMode}
      trainSeries={trainSeries}
      onClose={onClose}
    />
  );
};

export const ProductionDeviationModal: FC<ProductionDeviationModalProps> = ({
  defaultValues,
  ...props
}) => {
  const isEditMode = !!defaultValues;
  const formMethods = useFormVerbose<ProductionDeviationForm>({
    resolver: zodResolver(getFormSchema(isEditMode)),
    defaultValues,
  });

  return (
    <FormProvider {...formMethods}>
      <ProductionDeviationModalContent
        defaultValues={defaultValues}
        {...props}
      />
    </FormProvider>
  );
};
